import { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  useColorModeValue,
  Button,
  HStack,
  Grid,
  Icon,
  Flex,
  Spinner,
} from '@chakra-ui/react';
import { FiArrowLeft, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useNavigate, Link } from 'react-router-dom';
import { obtenerUltimasActualizaciones, type ActualizacionProcesada } from '../config/index';
import { formatearFechaCompleta } from '../utils/dateUtils';

export const UltimasActualizaciones = () => {
  const [actualizaciones, setActualizaciones] = useState<ActualizacionProcesada[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pagina, setPagina] = useState<number>(1);
  const [elementosPorPagina, setElementosPorPagina] = useState<number>(10);
  
  const navigate = useNavigate();
  const brandRed = useColorModeValue('red.500', 'red.300');

  // Cargar actualizaciones optimizadas desde el backend
  useEffect(() => {
    const cargarActualizaciones = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const startTime = performance.now();
        console.log('🚀 Iniciando carga de actualizaciones...');
        
        // Cargar hasta 100 actualizaciones (ya ordenadas y filtradas en el backend)
        const data = await obtenerUltimasActualizaciones(100);
        
        const endTime = performance.now();
        const loadTime = ((endTime - startTime) / 1000).toFixed(2);
        console.log(`✅ Actualizaciones cargadas en ${loadTime}s (${data.length} entradas)`);
        
        setActualizaciones(data);
        setIsLoading(false);
      } catch (error) {
        console.error('❌ Error al cargar actualizaciones:', error);
        setError('Error al cargar los datos de actualizaciones. Por favor, inténtelo de nuevo más tarde.');
        setIsLoading(false);
      }
    };
    cargarActualizaciones();
  }, []);

  // Calcular paginación
  const totalPaginas = Math.max(1, Math.ceil(actualizaciones.length / elementosPorPagina));
  const inicio = (pagina - 1) * elementosPorPagina;
  const fin = inicio + elementosPorPagina;
  const actualizacionesPaginadas = actualizaciones.slice(inicio, fin);

  // Función para cambiar de página
  const cambiarPagina = (nuevaPagina: number) => {
    if (nuevaPagina > 0 && nuevaPagina <= totalPaginas) {
      setPagina(nuevaPagina);
      window.scrollTo(0, 0);
    }
  };

  // Función para cambiar el número de elementos por página
  const cambiarElementosPorPagina = (nuevaCantidad: number) => {
    setElementosPorPagina(nuevaCantidad);
    setPagina(1);
  };

  return (
    <Box 
      minH="100vh" 
      backgroundImage="url('/silla.jpg')"
      backgroundSize="cover"
      backgroundPosition="center"
      position="relative"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="whiteAlpha.700"
      />
      <Container maxW="container.xl" position="relative" zIndex={1} py={8}>
        <VStack spacing={8} align="stretch">
          <Box 
            position="relative"
            bg="whiteAlpha.500"
            p={{ base: 2, md: 3 }}
            borderRadius="xl"
            backdropFilter="blur(10px)"
            display="flex"
            alignItems="center"
            minH={{ base: "60px", md: "70px" }}
          >
            <Link to="/">
              <Box 
                as="img" 
                src="/famma.png" 
                alt="Logo Famma" 
                className="logo" 
                h={{ base: "45px", md: "55px" }}
                objectFit="contain"
                transition="transform 0.2s ease"
                _hover={{ transform: 'scale(1.05)' }}
                ml={{ base: 2, md: 3 }}
              />
            </Link>
            <Box flex="1" textAlign="center">
              <Heading
                fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                fontWeight="bold"
                color="brand.600"
                letterSpacing="tight"
                lineHeight="shorter"
              >
                Últimas Actualizaciones
              </Heading>
            </Box>
            <Box w={{ base: "45px", md: "55px" }} /> {/* Spacer para balancear el logo */}
          </Box>

          <HStack justify="space-between" align="center">
            <Heading color={brandRed}>Últimas Actualizaciones</Heading>
            <Button
              leftIcon={<Icon as={FiArrowLeft} />}
              colorScheme="red"
              variant="outline"
              onClick={() => navigate('/dashboard')}
            >
              Volver al Dashboard
            </Button>
          </HStack>

          <Box w="100%" p={6} bg="white" borderRadius="xl" boxShadow="lg">
            {isLoading ? (
              <Box textAlign="center" py={8}>
                <Spinner size="xl" color="red.500" mb={4} />
                <Text fontSize="xl" color="gray.500">
                  Cargando actualizaciones...
                </Text>
              </Box>
            ) : error ? (
              <Box textAlign="center" py={8}>
                <Text fontSize="xl" color="red.500" mb={4}>
                  {error}
                </Text>
                <Button colorScheme="red" onClick={() => window.location.reload()}>
                  Reintentar
                </Button>
              </Box>
            ) : actualizaciones.length === 0 ? (
              <Box textAlign="center" py={8}>
                <Text fontSize="xl" color="gray.500">
                  No hay actualizaciones recientes
                </Text>
                <Text fontSize="md" color="gray.400" mt={2}>
                  No se encontraron entradas de historial en los expedientes.
                </Text>
                <Button mt={4} colorScheme="red" onClick={() => window.location.reload()}>
                  Actualizar página
                </Button>
              </Box>
            ) : (
              <Box>
                {/* Información de paginación superior */}
                <Box py={3} px={4} bg="gray.100" borderRadius="md" mb={4}>
                  <HStack justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={3}>
                    <Text fontSize="sm" fontWeight="medium">
                      Mostrando {actualizacionesPaginadas.length} de {actualizaciones.length} actualizaciones 
                      (página {pagina} de {totalPaginas})
                    </Text>
                    <HStack spacing={2}>
                      <Text fontSize="sm">Mostrar:</Text>
                      <Button
                        size="sm"
                        colorScheme={elementosPorPagina === 5 ? "red" : "gray"}
                        onClick={() => cambiarElementosPorPagina(5)}
                      >
                        5
                      </Button>
                      <Button
                        size="sm"
                        colorScheme={elementosPorPagina === 10 ? "red" : "gray"}
                        onClick={() => cambiarElementosPorPagina(10)}
                      >
                        10
                      </Button>
                      <Button
                        size="sm"
                        colorScheme={elementosPorPagina === 20 ? "red" : "gray"}
                        onClick={() => cambiarElementosPorPagina(20)}
                      >
                        20
                      </Button>
                    </HStack>
                  </HStack>
                </Box>
                
                {/* Lista de actualizaciones */}
                {actualizacionesPaginadas.map((historial, index) => (
                  <Box 
                    key={`${historial.expedienteId}-${index}`} 
                    p={6} 
                    bg="white" 
                    borderRadius="lg" 
                    mb={4}
                    cursor="pointer"
                    boxShadow="sm"
                    onClick={() => navigate(`/expediente/${historial.expedienteId}`)}
                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
                    transition="all 0.2s"
                    borderWidth="1px"
                    borderColor="gray.100"
                  >
                    <HStack justify="space-between" mb={3}>
                      <Text fontWeight="bold" color={brandRed} fontSize="xl">
                        Expediente: {historial.expedienteId}
                      </Text>
                      <Text 
                        px={3} 
                        py={1} 
                        borderRadius="full" 
                        bg={
                          historial.estado === 'Resuelto' ? 'green.100' : 
                          historial.estado === 'En trámite' ? 'orange.100' : 
                          'gray.100'
                        }
                        color={
                          historial.estado === 'Resuelto' ? 'green.700' : 
                          historial.estado === 'En trámite' ? 'orange.700' : 
                          'gray.700'
                        }
                        fontSize="md"
                        fontWeight="medium"
                      >
                        {historial.estado}
                      </Text>
                    </HStack>
                    
                    <Text fontSize="lg" mb={4} color="gray.700">
                      {historial.cambio}
                    </Text>
                    
                    <Grid templateColumns="repeat(3, 1fr)" gap={6} mb={4}>
                      <Box>
                        <Text fontSize="sm" color="gray.500">Tipo de Barrera</Text>
                        <Text fontSize="md" fontWeight="medium">{historial.tipoBarrera}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.500">Fecha última actualización</Text>
                        <Text fontSize="md" fontWeight="medium">{formatearFechaCompleta(historial.fechaUltimaActualizacion)}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.500">Actualizado por</Text>
                        <Text fontSize="md" fontWeight="medium">{historial.usuario}</Text>
                      </Box>
                    </Grid>
                    
                    {historial.informacionDenunciante && (
                      <Box mt={3} p={4} bg="blue.50" borderRadius="lg">
                        <Text fontSize="md" color="blue.700">
                          Información adicional: {historial.informacionDenunciante}
                        </Text>
                      </Box>
                    )}
                  </Box>
                ))}
                
                {/* Controles de paginación inferior */}
                {totalPaginas > 1 && (
                  <Flex justify="center" align="center" mt={6} mb={2}>
                    <Button
                      size="sm"
                      leftIcon={<Icon as={FiChevronLeft} />}
                      mr={2}
                      onClick={() => cambiarPagina(pagina - 1)}
                      isDisabled={pagina === 1}
                      colorScheme="gray"
                    >
                      Anterior
                    </Button>
                    
                    {/* Mostrar números de página */}
                    <HStack spacing={1}>
                      {/* Primera página siempre visible */}
                      {pagina > 3 && (
                        <Button
                          size="sm"
                          onClick={() => cambiarPagina(1)}
                          colorScheme={pagina === 1 ? "red" : "gray"}
                          variant={pagina === 1 ? "solid" : "outline"}
                        >
                          1
                        </Button>
                      )}
                      
                      {/* Ellipsis si hay muchas páginas antes */}
                      {pagina > 4 && (
                        <Text mx={1}>...</Text>
                      )}
                      
                      {/* Páginas anteriores a la actual */}
                      {pagina > 2 && (
                        <Button
                          size="sm"
                          onClick={() => cambiarPagina(pagina - 2)}
                          colorScheme="gray"
                          variant="outline"
                        >
                          {pagina - 2}
                        </Button>
                      )}
                      
                      {pagina > 1 && (
                        <Button
                          size="sm"
                          onClick={() => cambiarPagina(pagina - 1)}
                          colorScheme="gray"
                          variant="outline"
                        >
                          {pagina - 1}
                        </Button>
                      )}
                      
                      {/* Página actual */}
                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="solid"
                      >
                        {pagina}
                      </Button>
                      
                      {/* Páginas posteriores a la actual */}
                      {pagina < totalPaginas && (
                        <Button
                          size="sm"
                          onClick={() => cambiarPagina(pagina + 1)}
                          colorScheme="gray"
                          variant="outline"
                        >
                          {pagina + 1}
                        </Button>
                      )}
                      
                      {pagina < totalPaginas - 1 && (
                        <Button
                          size="sm"
                          onClick={() => cambiarPagina(pagina + 2)}
                          colorScheme="gray"
                          variant="outline"
                        >
                          {pagina + 2}
                        </Button>
                      )}
                      
                      {/* Ellipsis si hay muchas páginas después */}
                      {pagina < totalPaginas - 3 && (
                        <Text mx={1}>...</Text>
                      )}
                      
                      {/* Última página siempre visible */}
                      {pagina < totalPaginas - 2 && (
                        <Button
                          size="sm"
                          onClick={() => cambiarPagina(totalPaginas)}
                          colorScheme={pagina === totalPaginas ? "red" : "gray"}
                          variant={pagina === totalPaginas ? "solid" : "outline"}
                        >
                          {totalPaginas}
                        </Button>
                      )}
                    </HStack>
                    
                    <Button
                      size="sm"
                      rightIcon={<Icon as={FiChevronRight} />}
                      ml={2}
                      onClick={() => cambiarPagina(pagina + 1)}
                      isDisabled={pagina === totalPaginas}
                      colorScheme="gray"
                    >
                      Siguiente
                    </Button>
                  </Flex>
                )}
              </Box>
            )}
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};
